import styled from "styled-components";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/*
 * GlassCard — reusable frosted-glass surface with roll-up / roll-down transitions.
 *
 * Primary (declarative) usage — change `contentKey` when the content changes in a
 * "major" way. The card rolls up, swaps to the new children, then rolls down:
 *
 *   <GlassCard contentKey={activeMenu}>{menus[activeMenu]}</GlassCard>
 *
 * Minor changes (same key) just resize smoothly with no roll.
 *
 * Escape hatch (imperative) — for cases not tied to a content swap:
 *
 *   const card = useRef();
 *   await card.current.rollUp();   // stays collapsed until rollDown()
 *   await card.current.rollDown();
 *   await card.current.replay();   // up + down
 *
 * Phases: "idle" → "up" (collapsing) → "down" (expanding) → "idle"
 */

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia(REDUCED_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(REDUCED_QUERY);
    const onChange = (e) => setReduced(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/* ── styles ────────────────────────────────────────────────────────── */

const Shell = styled.section`
  position: relative;
  z-index: 10;
  box-sizing: content-box; /* height = measured content; the 1px border sits outside it */
  width: ${(p) => p.$width};
  overflow: hidden;
  border-radius: 20px;
  background: ${(p) => p.$tint};
  backdrop-filter: blur(${(p) => p.$blur}px);
  -webkit-backdrop-filter: blur(${(p) => p.$blur}px);
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
  transition:
    height ${(p) => p.$ms}ms ${EASE},
    opacity ${(p) => p.$ms}ms ease-out;
`;

const Content = styled.div`
  padding: ${(p) => p.$padding};
  text-align: center;
  clip-path: ${(p) => (p.$clipped ? "inset(0 0 100% 0)" : "inset(0 0 0 0)")};
  opacity: ${(p) => (p.$clipped ? 0 : 1)};
  pointer-events: ${(p) => (p.$locked ? "none" : "auto")};
  transition:
    clip-path ${(p) => p.$ms}ms ${EASE},
    opacity ${(p) => Math.round(p.$ms * 0.8)}ms ease-out;

  &:focus {
    outline: none;
  }
`;

/* ── component ─────────────────────────────────────────────────────── */

const GlassCard = forwardRef(function GlassCard(
  {
    children,
    contentKey,
    duration = 320, // ms per half (up or down)
    collapsedHeight = 0, // px the shell shrinks to while rolled up
    enterDelay = null, // ms; if set, the card mounts collapsed and rolls down after this
    width = "min(280px, 70vw)",
    padding = "34px 38px",
    tint = "rgba(44, 47, 51, 0.005)",
    blur = 5,
    onPhaseChange,
    style,
    ...rest
  },
  ref
) {
  const reduced = usePrefersReducedMotion();
  const ms = reduced ? 0 : duration;

  const [phase, setPhase] = useState(enterDelay == null ? "idle" : "up");
  const [hasEntered, setHasEntered] = useState(enterDelay == null);
  const [shownKey, setShownKey] = useState(contentKey);
  const [contentHeight, setContentHeight] = useState(null);

  const contentRef = useRef(null);

  // Keep rendering the OLD children while rolling up; switch once collapsed.
  // (Ref write during render is idempotent here, so StrictMode double-render is harmless.)
  const snapshot = useRef(children);
  if (contentKey === shownKey) snapshot.current = children;

  // Measure content so the shell can animate between pixel heights.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight;
      setContentHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Entrance roll-down.
  useEffect(() => {
    if (enterDelay == null) return;
    const t = setTimeout(() => {
      setHasEntered(true);
      setPhase("down");
    }, enterDelay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Major content change: up → swap → down. Rapid key changes restart cleanly
  // and always settle on the latest key.
  useEffect(() => {
    if (contentKey === shownKey) return;
    if (ms === 0 || !hasEntered) {
      setShownKey(contentKey);
      return;
    }
    const hadFocus = contentRef.current?.contains(document.activeElement);
    setPhase("up");
    const t = setTimeout(() => {
      setShownKey(contentKey);
      setPhase("down");
      // The focused element just got unmounted — don't drop keyboard/screen-reader users on <body>.
      if (hadFocus) requestAnimationFrame(() => contentRef.current?.focus({ preventScroll: true }));
    }, ms);
    return () => clearTimeout(t);
  }, [contentKey, shownKey, ms, hasEntered]);

  // Settle after rolling down.
  useEffect(() => {
    if (phase !== "down") return;
    const t = setTimeout(() => setPhase("idle"), ms);
    return () => clearTimeout(t);
  }, [phase, ms]);

  useEffect(() => {
    onPhaseChange?.(phase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useImperativeHandle(
    ref,
    () => ({
      rollUp: async () => {
        setPhase("up");
        await wait(ms);
      },
      rollDown: async () => {
        setHasEntered(true);
        setPhase("down");
        await wait(ms);
      },
      replay: async () => {
        setPhase("up");
        await wait(ms);
        setHasEntered(true);
        setPhase("down");
        await wait(ms);
      },
    }),
    [ms]
  );

  const collapsed = phase === "up";
  const height =
    contentHeight == null ? undefined : collapsed ? collapsedHeight : contentHeight;

  return (
    <Shell
      $width={width}
      $tint={tint}
      $blur={blur}
      $ms={ms}
      aria-busy={phase !== "idle"}
      // Inline, not styled props: per-pixel values would mint a new class every frame.
      style={{ ...style, height, opacity: hasEntered ? 1 : 0 }}
      {...rest}
    >
      <Content
        ref={contentRef}
        tabIndex={-1}
        $padding={padding}
        $clipped={collapsed}
        $locked={phase !== "idle"}
        $ms={ms}
      >
        {snapshot.current}
      </Content>
    </Shell>
  );
});

export default GlassCard;
